'use client';

import { useTranslation } from 'next-i18next';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Button, Collapsible, Flex, Separator, Text, useCollapsible } from '@chakra-ui/react';

import { Input } from '@nocowanie/common-ui/components/atoms/input';
import { Switch } from '@nocowanie/common-ui/components/molecules/switch';
import { mapToaster } from '@nocowanie/common-ui/components/molecules/toaster';
import { IconsLinear } from '@nocowanie/icons/linear';

import { useMapDrawing } from '@app/components/molecules/map-edit-mode-sidebar';
import { HttpMethods } from '@app/enums';
import {
    convertApiPolygonsToGeoJsonFeatures,
    convertGeoJsonPolygons,
    validateImportedPolygons,
} from '@app/helpers/map-polygons.helpers';
import { useMapSearch } from '@app/hooks/useMapSearch';
import { MapCenter } from '@app/models';
import { MapPolygonsBody } from '@app/models/map-polygons.model';
import { getMapPolygons, sendMapPolygons } from '@app/state/api-thunks/map-accommodations.thunk';

import { MapCenterInputs } from './map-center-inputs';
import { DrawMode } from './useMapDrawing.props';
import { usePolygonsImportExport } from './usePolygonsImportExport';

export const MapEditModeSidebar = ({ map }: { map: google.maps.Map | null }) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'map.editMode',
    });
    const { searchCriteria } = useMapSearch();
    const [isEditMode, setIsEditMode] = useState(false);
    const { polygons, mode, setMode, clear, undo, importPolygons } = useMapDrawing({
        map,
        enabled: isEditMode,
    });

    const drawModes: { mode: DrawMode; title: string; icon: JSX.Element }[] = [
        { mode: DrawMode.Polygon, title: t('polygon'), icon: <IconsLinear.Polygon /> },
        { mode: DrawMode.Freehand, title: t('freehand'), icon: <IconsLinear.Stick /> },
        { mode: DrawMode.Select, title: t('select'), icon: <IconsLinear.MouseSquare /> },
    ];

    const [prevLocation, setPrevLocation] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);

    const [hasExistingData, setHasExistingData] = useState<boolean | null>(null);
    const [center, setCenter] = useState<MapCenter>(null);

    const polygonsJson = useMemo(() => JSON.stringify(polygons, null, 2), [polygons]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const centerEditor = useCollapsible();

    const showToast = useCallback((message: string) => {
        mapToaster.create({ description: message });
    }, []);

    const { handleImport, handleExport } = usePolygonsImportExport({
        polygons,
        polygonsJson,
        clear,
        importPolygons,
    });

    const handleSend = async () => {
        const { geoId, locationType } = searchCriteria;
        if (!geoId || !locationType) return;

        if (
            !center ||
            center.lat == null ||
            center.lon == null ||
            center.lat < -90 ||
            center.lat > 90 ||
            center.lon < -180 ||
            center.lon > 180
        ) {
            showToast(t('errors.centerError'));
            return;
        }

        setIsSending(true);
        try {
            const areaPolygons = convertGeoJsonPolygons(polygons);

            const body: MapPolygonsBody = {
                ...(areaPolygons.length > 0 ? { polygons: areaPolygons } : {}),
                center: { lat: center.lat, lon: center.lon },
            };

            const response = await sendMapPolygons(
                geoId,
                locationType,
                body,
                hasExistingData ? HttpMethods.PATCH : HttpMethods.POST,
            );

            if (response?.status === 200 || response?.status === 201) {
                window.location.reload();
            }
        } catch (err) {
            console.error('[Map Edit Mode] sending polygons error:', err);
        } finally {
            setIsSending(false);
        }
    };

    const handleFetch = async () => {
        const { geoId, locationType } = searchCriteria;
        if (!geoId || !locationType) return;

        try {
            const response = await getMapPolygons(geoId, locationType);

            if (response?.center) {
                setCenter({ lat: response.center.lat, lon: response.center.lon });
            } else {
                setCenter(null);
            }

            setHasExistingData(!!response);

            if (!response?.polygons?.length) {
                return;
            }

            const features = convertApiPolygonsToGeoJsonFeatures(response);

            const validated = validateImportedPolygons(features);
            if (!validated) {
                console.warn('[Map Edit Mode] fetched polygons are invalid');
                return;
            }

            importPolygons(validated);
        } catch (err) {
            console.error('[Map Edit Mode] fetching polygons error:', err);
            setHasExistingData(false);
        }
    };

    useEffect(() => {
        if (!searchCriteria?.location) return;

        if (prevLocation && prevLocation !== searchCriteria.location) {
            clear();
            setCenter(null);
        }

        setPrevLocation(searchCriteria.location);
    }, [searchCriteria?.location, prevLocation]);

    useEffect(() => {
        if (isEditMode) {
            handleFetch();
        }
    }, [isEditMode, prevLocation]);

    return (
        <>
            <Box
                position="absolute"
                bottom={6}
                right={16}
                bg="whiteAlpha.900"
                p={2}
                borderRadius="md"
                zIndex={'sticky'}
                boxShadow="md"
            >
                <Switch
                    checked={isEditMode}
                    onChange={() => setIsEditMode(prev => !prev)}
                    labelRight={t('switchLabel')}
                    size="md"
                />
            </Box>

            {isEditMode && (
                <Flex
                    position="absolute"
                    bottom={28}
                    right={2}
                    w="324px"
                    h="70%"
                    bg="whiteAlpha.900"
                    p={2}
                    borderRadius="md"
                    flexDirection="column"
                    boxShadow="md"
                    zIndex={'sticky'}
                >
                    <Text fontSize="sm" fontWeight="bold" mb={2}>
                        {t('tools')}
                    </Text>
                    <Flex alignItems={'center'} gap={2} mb={2}>
                        {drawModes.map(item => (
                            <Button
                                key={item.mode}
                                size="xs"
                                variant={mode === item.mode ? 'solid' : 'outline'}
                                onClick={() => setMode(item.mode)}
                                title={item.title}
                            >
                                {item.icon}
                            </Button>
                        ))}
                        <Separator orientation="vertical" height="6" />
                        <Button
                            size="xs"
                            disabled={polygons.length === 0}
                            onClick={undo}
                            title={t('undo')}
                        >
                            <IconsLinear.UndoArrow />
                        </Button>
                        <Button
                            size="xs"
                            disabled={polygons.length === 0}
                            onClick={clear}
                            title={t('clear')}
                        >
                            <IconsLinear.BagCross />
                        </Button>
                        <Separator orientation="vertical" height="6" />
                        <Button
                            size="xs"
                            onClick={() => centerEditor.setOpen(!centerEditor.open)}
                            title="Edytuj centrum"
                        >
                            <IconsLinear.Location />
                            {centerEditor.open ? (
                                <IconsLinear.ArrowUp2 />
                            ) : (
                                <IconsLinear.ArrowDown2 size="xs" />
                            )}
                        </Button>
                    </Flex>
                    <Collapsible.RootProvider value={centerEditor}>
                        <Collapsible.Content>
                            <Flex flexDirection="column" gap={2} my={1}>
                                <MapCenterInputs value={center} onChange={setCenter} />
                            </Flex>
                        </Collapsible.Content>
                    </Collapsible.RootProvider>
                    <Separator marginY={2} />
                    <Flex gap={4}>
                        <Button
                            onClick={handleSend}
                            size="sm"
                            colorPalette={'secondary'}
                            disabled={
                                isSending || !center || center?.lat === null || center?.lon === null
                            }
                            flex="1"
                        >
                            {t('send')}
                        </Button>
                        <Flex gap={1}>
                            <Button
                                size="xs"
                                disabled={polygons.length === 0}
                                onClick={handleExport}
                                title={t('export')}
                            >
                                <IconsLinear.ExportCircle />
                            </Button>
                            <Button
                                size="xs"
                                onClick={() => fileInputRef.current?.click()}
                                title={t('import')}
                            >
                                <IconsLinear.ImportCircle />
                            </Button>
                            <Input
                                ref={fileInputRef}
                                onChange={handleImport}
                                inputProps={{
                                    type: 'file',
                                    accept: '.json',
                                }}
                                hidden
                            />
                        </Flex>
                    </Flex>
                    {polygons.length ? (
                        <>
                            <Text mt={3} fontSize="sm" fontWeight={'bold'} color={'gray.700'}>
                                {t('editedGeography')}:
                            </Text>
                            <Text fontSize="sm">
                                {searchCriteria.location} ({t('id')}: {searchCriteria.geoId},{' '}
                                {t('type')}: {searchCriteria.locationType})
                            </Text>
                            <Separator marginY={2} />
                            <Box flex="1" overflowY="auto" paddingRight="2">
                                <pre style={{ fontSize: '10px' }}>{polygonsJson}</pre>
                            </Box>
                        </>
                    ) : null}
                </Flex>
            )}
        </>
    );
};
